/* eslint-disable global-require */
import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, Navigate } from 'react-router';

import authorization from './components/Auth/AuthorizationComponent';
import Loader from './components/Loader/Loader';
import ConversationRouteFallback from './modules/Conversation/components/preview/ConversationRouteFallback';
import CartRouteFallback from './modules/Cart/components/view/CartRouteFallback';
import App from './modules/App/App';

// Role Filter
const admin = authorization(['admin']);

const customer = authorization(['customer']);
const customerUp = authorization(['customer', 'admin']);

const baseRoute = '';

function getPath(path = '', isConsole = false) {
    return `${baseRoute}/${isConsole ? 'console' : ''}${path && isConsole ? '/' : ''}${path || ''}`;
}

const setAdminAuth = adminRoute => ({ auth: ['admin'], ...adminRoute, children: (adminRoute.children || []).map(setAdminAuth) });

const UserListPage = lazy(() => import('./modules/User/pages/UserListPage/UserListPage'));
const UserEditPage = lazy(() => import('./modules/User/pages/UserEditPage/UserEditPage'));
const ConfigPage = lazy(() => import('./modules/Config/pages/ConfigPage/ConfigPage'));

const HomePage = lazy(() => import('./modules/App/pages/view/HomePage'));
const ErrorPage = lazy(() => import('./modules/Error/pages/ErrorPage'));
const ContactPage = lazy(() => import('./modules/Contact/pages/ContactPage/ContactPage'));

const UserLoginPage = lazy(() => import('./modules/User/pages/UserLoginPage/UserLoginPage'));
const UserRegisterPage = lazy(() => import('./modules/User/pages/UserRegisterPage/UserRegisterPage'));
const UserConfirmEmailPage = lazy(() => import('./modules/User/pages/UserConfirmEmailPage/UserConfirmEmailPage'));
const UserMagicPage = lazy(() => import('./modules/User/pages/UserMagicPage/UserMagicPage'));
const UserProfilePage = lazy(() => import('./modules/User/pages/UserProfilePage/UserProfilePage'));

const OfferPage = lazy(() => import('./modules/Offer/pages/OfferPage'));

const AddressListPage = lazy(() => import('./modules/Address/pages/list/AddressListPage'));
const AddressEditPage = lazy(() => import('./modules/Address/pages/edit/AddressEditPage'));

const CartPage = lazy(() => import('./modules/Cart/pages/view/CartPage'));

const ConversationListAdminPage = lazy(() => import('./modules/Conversation/pages/list/ConversationListAdminPage'));
const ConversationListPage = lazy(() => import('./modules/Conversation/pages/list/ConversationListPage'));
const ConversationMediaViewerPage = lazy(() => import('./modules/Conversation/pages/view/ConversationMediaViewerPage'));
const ConversationEditPage = lazy(() => import('./modules/Conversation/pages/edit/ConversationEditPage'));
const ConversationHighlightPage = lazy(() => import('./modules/Conversation/pages/edit/ConversationHighlightPage'));

const OrderListPage = lazy(() => import('./modules/Order/pages/list/OrderListPage'));
const OrderViewPage = lazy(() => import('./modules/Order/pages/view/OrderViewPage'));
const OrderListAdminPage = lazy(() => import('./modules/Order/pages/list/OrderListAdminPage'));
const OrderViewAdminPage = lazy(() => import('./modules/Order/pages/view/OrderViewAdminPage'));
const OrderAddPage = lazy(() => import('./modules/Order/pages/edit/OrderAddPage'));

const ProductAdminListPage = lazy(() => import('./modules/Product/pages/list/ProductListAdminPage'));
const ProductAdminEditPage = lazy(() => import('./modules/Product/pages/edit/ProductEditPage'));

const ShippingListPage = lazy(() => import('./modules/Shipping/pages/list/ShippingListPage'));
const ShippingEditPage = lazy(() => import('./modules/Shipping/pages/edit/ShippingEditPage'));

const VatPage = lazy(() => import('./modules/Vat/pages/VatPage/VatPage'));

const VoucherListPage = lazy(() => import('./modules/Voucher/pages/list/VoucherListPage'));
const VoucherEditPage = lazy(() => import('./modules/Voucher/pages/edit/VoucherEditPage'));

const AffiliationListPage = lazy(() => import('./modules/Affiliation/pages/list/AffiliationListPage'));

const Dashboard = lazy(() => import('./modules/Dashboard/pages/DashboardPage'));

export const routesData = []
    .concat([
		{
			path: getPath('', true),
			component: Dashboard,
		},
        {
            path: getPath('config', true),
            component: ConfigPage,
        },
        {
            path: getPath('conversations', true),
            component: ConversationListAdminPage,
			auth: ['admin', 'printer'],
        },
        {
            path: getPath('orders', true),
            component: OrderListAdminPage,
            auth: ['printer', 'manager', 'admin'],
            children: [
                {
                    path: ':status',
                    component: OrderListAdminPage,
                    auth: ['printer', 'manager', 'admin'],
                },
                {
                    path: 'conversation',
                    component: OrderListAdminPage,
                    auth: ['printer', 'manager', 'admin'],
                    children: [
                        {
                            path: ':conversationId',
                            component: OrderListAdminPage,
                            auth: ['printer', 'manager', 'admin'],
                        },
                    ],
                },
                {
                    path: 'user',
                    component: OrderListAdminPage,
                    auth: ['manager', 'admin'],
                    children: [
                        {
                            path: ':userId',
                            component: OrderListAdminPage,
                            auth: ['manager', 'admin'],
                        },
                    ],
                },
            ],
        },
        {
            path: getPath('order', true),
            component: OrderAddPage,
			auth: ['manager', 'admin'],
        },
        {
            path: getPath('order/:id', true),
            component: OrderViewAdminPage,
            auth: ['manager', 'admin'],
        },
        {
            path: getPath('products', true),
            component: ProductAdminListPage,
        },
        {
            path: getPath('product', true),
            component: ProductAdminEditPage,
			children: [{
				path: ':id',
				component: ProductAdminEditPage,
			}],
        },
        {
            path: getPath('shippings', true),
            component: ShippingListPage,
        },
        {
            path: getPath('shipping', true),
            component: ShippingEditPage,
            children: [
                {
                    path: ':id',
                    component: ShippingEditPage,
                },
            ],
        },
        {
            path: getPath('users', true),
            component: UserListPage,
			auth: ['manager', 'admin'],
        },
        {
            path: getPath('user', true),
            component: UserEditPage,
			auth: ['manager', 'admin'],
			children: [
				{
					path: ':_id',
					component: UserEditPage,
					auth: ['manager', 'admin'],
				},
			],
        },
        {
            path: getPath('vat', true),
            component: VatPage,
            children: [
                {
                    path: 'report',
                    component: VatPage,
                },
            ],
        },
        {
            path: getPath('vouchers', true),
            component: VoucherListPage,
        },
        {
            path: getPath('voucher', true),
            component: VoucherEditPage,
            children: [
                {
                    path: ':id',
                    component: VoucherEditPage,
                },
            ],
        },
    ]).map(setAdminAuth)
    .concat([
        {
            path: getPath('address'),
            component: AddressEditPage,
            auth: [],
            children: [
                {
                    path: ':id',
                    component: AddressEditPage,
                },
            ],
        },
        {
            path: getPath('addresses'),
            component: AddressListPage,
            auth: [],
        },
        {
            path: getPath('cart'),
            component: CartPage,
            suspenseFallback: CartRouteFallback,
            children: [
                {
                    path: ':step',
                    component: CartPage,
                    children: [
                        {
                            path: ':orderId',
                            component: CartPage,
                        },
                    ],
                },
            ],
        },
        {
            path: getPath('contact'),
            component: ContactPage,
        },
        {
            path: getPath('conversations'),
            component: ConversationListPage,
            auth: [],
        },
        {
			path: getPath('conversation/:_id/viewer/:data'),
			component: ConversationMediaViewerPage,
            auth: [],
		},
        {
			path: getPath('conversation/:_id/highlights'),
			component: ConversationHighlightPage,
            auth: [],
		},
        {
            path: getPath('conversation/:_id'),
            component: ConversationEditPage,
            auth: [],
            suspenseFallback: ConversationRouteFallback,
            children: [
                {
                    path: ':step',
                    component: ConversationEditPage,
					auth: [],
                },
            ],
        },
        {
            path: getPath('orders'),
            component: OrderListPage,
            auth: [],
        },
        {
            path: getPath('order/:orderCode'),
            component: OrderViewPage,
            auth: [],
        },
        {
            path: getPath('affiliations'),
            component: AffiliationListPage,
            auth: [],
        },
        {
            path: getPath('user/login'),
            component: UserLoginPage,
        },
        {
            path: getPath('user/profile'),
            component: UserProfilePage,
        },
        {
            path: getPath('user/confirm-email'),
            component: UserConfirmEmailPage,
        },
        {
            path: getPath('user/magic'),
            component: UserMagicPage,
        },
        {
            path: getPath('offer'),
            component: OfferPage,
        },
        {
            path: getPath('offer2'),
            component: OfferPage,
        },
        {
            path: getPath('blog'),
            component: OfferPage,
        },
        {
            path: getPath('user/register'),
            component: UserRegisterPage,
            children: [
                {
                    path: ':success',
                    component: UserRegisterPage,
                },
            ],
        },
        {
            path: getPath('user/resetpassword'),
            component: UserLoginPage,
        },
        {
            path: getPath(''),
            component: HomePage,
        },
        {
			path: '*',
			component: ErrorPage,
        },
    ]).map(route => ({
        ...route,
        component: route.auth ? authorization(route.auth, <UserLoginPage />)(route.component) : route.component,
    }));

// console.log(routesData);

const renderRoutes = (routes, prefix = '') => {
    return routes.map((route, index) => {
        const path = `${prefix ? `${prefix}/` : ''}${route.path}`;
        const key = route.path || index;

        if(route.redirect) {
            return <Route key={key} path={path} element={<Navigate replace to={route.redirect} />} />;
        }
        // if(route.pagePath) {
        //     return (
        //         <Route key={index} {...route} render={props => <AsyncComponent path={route.pagePath} {...props} />} />
        //     );
        // }
        // prefix && console.log(prefix, route.path, path);
        if(route.noSuspense) {
            return <Route key={key} path={path} element={<route.component />} />;
        }
        const Fallback = route.suspenseFallback || Loader;
        return (
            <Route key={key} path={path} index={!route.path} element={<Suspense fallback={<Fallback />}><route.component /></Suspense>}>
                {route.children && route.children.length > 0 && renderRoutes(route.children, path)}
            </Route>
        );
    });
};

// react-router setup with code-splitting
function AppRoutes({ ssr = false }) {
    return (
        <Routes>
            <Route path="/" element={<App />}>
                {renderRoutes(routesData)}
            </Route>
        </Routes>
    );
}

AppRoutes.propTypes = {
    ssr: PropTypes.bool,
 };

export default AppRoutes;
